import { CommonModule } from '@angular/common';

import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';

import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { SiteQuotaConfig } from '../../../../core/models/card-quota/site-quota-config.model';

import { ClientIInfo } from '../../../../core/models/parking.model';

import { MapperSiteQuotaConfigService } from '../../../../core/mappers/card-quota/mapper-site-quota-config.service';

import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';

import { ApiService } from '../../../../core/services/api.service';

import { MenuPermissionService } from '../../../../core/services/menu-permission.service';

import { SiteStateService } from '../../../../core/services/site-state.service';



@Component({

  selector: 'app-setting-quota-card',

  standalone: true,

  imports: [CommonModule, FormsModule, PssLoadingComponent],

  templateUrl: './setting-quota-card.component.html',

  styleUrl: './setting-quota-card.component.css',

})

export class SettingQuotaCardComponent implements OnInit {

  readonly siteState = inject(SiteStateService);

  private readonly api = inject(ApiCardQuotaService);

  private readonly apiLegacy = inject(ApiService);

  private readonly mapper = inject(MapperSiteQuotaConfigService);

  private readonly toast = inject(ToastService);

  private readonly router = inject(Router);

  private readonly permissionService = inject(MenuPermissionService);

  private readonly cookieService = inject(CookieService);



  /** โมเดลที่ bind กับฟอร์ม — แสดงทันทีเมื่อเลือกลาน ไม่รอ API */

  formModel: SiteQuotaConfig | null = null;

  clients = signal<ClientIInfo[]>([]);

  isLoading = signal(false);

  isSaving = signal(false);

  isFirstSetup = signal(false);



  distributionModes = [

    { value: 'direct', label: 'Direct — แจกโควต้าตรง' },

    { value: 'via_distributor', label: 'Via Distributor — ผ่านฝ่ายอาคาร' },

  ];



  constructor() {

    effect(() => {

      const site = this.siteState.selectedSite();

      if (!site?.id) {

        untracked(() => {

          this.formModel = null;

          this.isFirstSetup.set(false);

        });

        return;

      }

      untracked(() => this.loadConfig());

    });

  }



  get selectedSiteId(): number {

    return this.siteState.selectedSite()?.id ?? 0;

  }



  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canSave(): boolean {
    return !!this.formModel && !!this.selectedSiteId && (this.isEditMode ? this.canUpdate : this.canCreate);
  }

  get canEditForm(): boolean {
    return this.isEditMode ? this.canUpdate : this.canCreate;
  }

  get isEditMode(): boolean {
    return (this.formModel?.id ?? 0) > 0;
  }



  ngOnInit(): void {

    if (this.selectedSiteId) this.loadConfig();

  }



  onRefresh(): void {

    this.loadConfig();

  }



  onSave(): void {

    if (!this.selectedSiteId) {

      this.toast.error('กรุณาเลือกลาน (Site) จากแถบด้านข้าง');

      return;

    }



    const cfg = this.formModel;

    if (!cfg) {

      this.toast.error('กรุณาเลือกลานก่อนบันทึก');

      return;

    }

    if (this.isEditMode) {
      if (!this.canUpdate) {
        this.toast.error('ไม่มีสิทธิ์บันทึก');
        return;
      }
    } else if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    if (cfg.distributionMode === 'via_distributor' && !cfg.distributorClientId) {
      this.toast.error('กรุณาเลือก Client ฝ่ายอาคาร (Distributor)');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapToPayload({ ...cfg, siteId: this.selectedSiteId }, userId, menuId);



    this.isSaving.set(true);

    this.api.saveSiteQuotaConfig(payload).subscribe({

      next: (res) => {

        const mapped = this.mapper.mapActionResponse(res);

        if (mapped.isSuccess) {

          this.toast.success(mapped.message || 'บันทึกสำเร็จ');

          this.loadConfig();

        } else {

          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');

        }

        this.isSaving.set(false);

      },

      error: () => {

        this.toast.error('เกิดข้อผิดพลาด');

        this.isSaving.set(false);

      },

    });

  }



  private loadConfig(): void {

    const siteId = this.selectedSiteId;

    if (!siteId) {

      this.formModel = null;

      this.isFirstSetup.set(false);

      return;

    }



    // แสดงฟอร์มทันทีด้วยค่า default — ไม่รอ API (กรณียังไม่เคยบันทึกจะได้ 404)

    this.formModel = this.createEmptyConfig(siteId);

    this.isFirstSetup.set(true);

    this.isLoading.set(true);



    this.apiLegacy.GetClientIInfo(siteId).subscribe({

      next: (res) => this.clients.set(res ?? []),

      error: () => this.clients.set([]),

    });



    this.api.getSiteQuotaConfig(siteId).subscribe({

      next: (res) => {

        if (res) {

          this.formModel = this.mapper.mapConfig(res as SiteQuotaConfig & Record<string, unknown>);

          this.isFirstSetup.set(false);

        }

        this.isLoading.set(false);

      },

      error: () => {

        this.isLoading.set(false);

        this.toast.error('โหลดการตั้งค่าโควต้าล้มเหลว — ยังสามารถตั้งค่าและบันทึกครั้งแรกได้');

      },

    });

  }



  private createEmptyConfig(siteId: number): SiteQuotaConfig {

    return {

      id: 0,

      siteId,

      siteName: this.siteState.selectedSite()?.name ?? '',

      distributionMode: 'direct',

      distributorClientId: null,

      distributorClientName: '',

      allocationRequiresApproval: false,

      tenantCanSelfManage: false,

      returnQuotaOnNaturalExpiry: true,

    };

  }



  private resolveUserId(): number {

    const fromLocal = localStorage.getItem('userId');

    if (fromLocal) return Number(fromLocal);

    return Number(this.cookieService.get('user_id')) || 0;

  }

}


